import { combineReducers } from 'redux';
import queryString from 'query-string';
import * as types from '../actions/actiontypes';
import { License, Log } from '../features/license/schema';

// Underneath it all, your Store is a database. The rule of the thumb is not to
// save data that can be calculated from the already existing data. Use e.g.
// Reselector if facing performance problems because of this. Using selectors
// is a good practice anyway.
// What makes this Conceptually a bit more fuzzy, is the fact that we use the
// Store for saving e.g. UI state. This does however not make the Store any less
// a database, and the same principle applies to this UI state data.
//
//   Don't save data that can be calculated from existing data.
//                                                             Most of the time.

const SESSION_STATES = {
  IDLE: 'IDLE',
  LOGGING_IN: 'LOGGING_IN',
  LOGGING_OUT: 'LOGGING_OUT',
}

const sessionInitialState = {
  logins: {},
  invalidLogin: false,
  state: SESSION_STATES.IDLE,
  tokenLoginChecked: false,
  states: SESSION_STATES,
};

function session(state = sessionInitialState, action) {
  switch (action.type) {
    case types.CLIENT.SET: {
      const { provider, token, client } = action.payload;
      const newState = JSON.parse(JSON.stringify(state));
      newState.logins[provider] = { token, client };
      newState.invalidLogin = false;
      newState.state = SESSION_STATES.IDLE;

      return newState;
    }
    case types.CLIENT.CLEAR: {
      return {
        ...state,
        logins: {
          ...state.logins,
          tilaajavastuu: null,
        },
        state: SESSION_STATES.LOGGING_OUT,
      };
    }
    case types.LOGIN.TOKENCHECKED:
      return {
        ...state,
        tokenLoginChecked: true,
        state: SESSION_STATES.IDLE,
      };
    case types.LOGIN.TOKEN:
    case types.LOGIN.COMPANY:
      return {
        ...state,
        state: SESSION_STATES.LOGGING_IN,
      };
    case types.AUTH.INVALID:
      return {
        ...state,
        invalidLogin: true,
        state: SESSION_STATES.IDLE,
      };
    default:
      return state;
  }
}

const licensesInitialState = {
  actionInProgress: false,
  actionLog: [],
  licenses: {
    fetched: null,
    pagination: {
      limit: 5,
      offset: 0,
      order: 'ASC',
      orderBy: 'license.startDate',
      searchId: null,
      total: -1,
    },
    filters: {
      dateFrom: null,
      dateTo: null,
      dateToFilter: 'startDate',
      searchString: '',
    },
    items: [],
  },
  // companyLicenses maps countryCode/businessId to object that holds
  // current state (fetching/idle) and a list of license ids
  companyLicenses: {},
  payments: {
    state: 'idle',
    byLicenseId: {},
  },
  logs: {
    fetched: null,
    pagination: {
      limit: 25,
      offset: 0,
      order: 'ASC',
      orderBy: 'licenseLog.startDate',
      total: -1,
    },
    filters: {
      dateFrom: null,
      dateTo: null,
      dateToFilter: 'startDate',
      dateNotSet: false,
      searchString: '',
    },
    items: [],
  },
  migration: {
    created: [],
    error: {
      status: false,
    },
    inProgress: false,
    invalidRows: [],
    overwrite: false,
    updated: null,
  },
  payment: { updated: null, inProgress: false, billed: [], paid: [] },
  sellers: { fetched: false, items: [] },
  types: { fetched: false, items: [] },
};

function licenses(state = licensesInitialState, action) {
  switch (action.type) {
    case types.LICENSES.FETCH:
      return {
        ...state,
        actionInProgress: true,
      };
    case types.LICENSES.RECEIVE: {
      const { payload: { licenses: fetchedLicenses, pagination }} = action;
      const items = fetchedLicenses.map(l => {
        l.timestamp = Date.now();
        return new License(l);
      });
      return {
        ...state,
        actionInProgress: false,
        licenses: {
          ...state.licenses,
          items,
          pagination,
          fetched: Date.now(),
        }
      };
    }
    case types.LICENSES.FAIL:
      return {
        ...state,
        actionInProgress: false,
        licenses: {
          ...state.licenses,
          fetched: true,
        }
      };
    case types.LICENSE_TYPES.RECEIVE:
      return {
        ...state,
        types: {
          ...state.types,
          fetched: true,
          items: action.payload.licenseTypes,
        }
      };
    case types.LICENSE_LOGS.RECEIVE: {
      const { payload: { licenseLogs: fetchedLogs, pagination }} = action;
      const items = fetchedLogs.map(l => {
        l.timestamp = Date.now();
        return new Log(l);
      });
      return {
        ...state,
        logs: {
          ...state.logs,
          items,
          pagination,
          fetched: Date.now(),
        }
      };
    }
    case types.LICENSE_LOGS.FAIL:
      return {
        ...state,
        logs: {
          ...state.logs,
          fetched: true,
        }
      };
    case types.COMPANY_LICENSES.FETCH: {
      const {
        payload: {
          country,
          identifier,
        },
      } = action;
      const {
        companyLicenses: {
          [`${country}/${identifier}`]: currentState
        }
      } = state;
      return {
        ...state,
        companyLicenses: {
          ...state.companyLicenses,
          [`${country}/${identifier}`]: {
            ...currentState,
            state: 'fetching',
            licenseIds: currentState ? currentState.licenseIds : [],
          },
        },
      };
    }
    case types.COMPANY_LICENSES.RECEIVE: {
      const {
        payload: {
          country,
          identifier,
          licenses: newLicenses,
        },
      } = action;
      const {
        licenses: {
          items: prevLicenses,
        },
        companyLicenses: {
          [`${country}/${identifier}`]: currentState,
        }
      } = state;
      const licenseIds = (newLicenses || []).map(l => l.id);

      // Take old licenses and exclude duplicates from newly fetched licenses.
      const items = prevLicenses.filter(
        prevLicense => !(newLicenses || []).find(
          newLicense => newLicense.id === prevLicense.id
        )
      );
      const newItems = (newLicenses || []).map(l => {
        l.timestamp = Date.now();
        return new License(l);
      });
      return {
        ...state,
        licenses: {
          ...state.licenses,
          items: [ ...items, ...newItems ],
          fetched: Date.now(),
        },
        companyLicenses: {
          ...state.companyLicenses,
          [`${country}/${identifier}`]: {
            ...currentState,
            licenseIds,
            state: 'idle',
          },
        },
      };
    }
    case types.COMPANY_LICENSES.FAIL: {
      const {
        payload: {
          country,
          identifier,
        },
      } = action;
      return {
        ...state,
        licenses: {
          ...state.licenses,
          fetched: true,
        },
        companyLicenses: {
          ...state.companyLicenses,
          [`${country}/${identifier}`]: {
            ...state.companyLicenses[`${country}/${identifier}`],
            state: 'idle',
          },
        },
      };
    }
    case types.LICENSE.SAVE: {
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.SAVE}-${Date.now()}`,
        level: 'info',
        read: true,
        messageId: 'license.alert.activation.default',
        defaultMessage: 'Activating license.',
      };
      return {
        ...state,
        actionLog: [log, ...state.actionLog],
        actionInProgress: true,
      };
    }
    case types.LICENSE.FAIL: {
      const { payload: { license }} = action;
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.FAIL}-${Date.now()}`,
        level: 'error',
        read: false,
        messageId: 'license.alert.activation.fail',
        defaultMessage: 'Oops! License activation failed.',
      };
      const items = [...state.licenses.items];
      if (license) {
        items.push(new License(license));
      }
      return {
        ...state,
        actionLog: [log, ...state.actionLog],
        actionInProgress: false,
        licenses: {
          ...state.licenses,
          fetched: Date.now(),
          items
        }
      };
    }
    case types.LICENSE.RESULT: {
      const { payload: { country, identifier, license } } = action;
      const {
        companyLicenses: {
          [`${country}/${identifier}`]: currentState,
        }
      } = state;
      const log = {
        created: Date.now(),
        id: `${types.LICENSE.RESULT}-${Date.now()}`,
        level: 'success',
        read: false,
        messageId: 'license.alert.activation.success',
        defaultMessage: 'License activated.',
      };
      return {
        ...state,
        actionLog: [log, ...state.actionLog],
        actionInProgress: false,
        licenses: {
          ...state.licenses,
          fetched: Date.now(),
          items: [...state.licenses.items, new License(license)]
        },
        companyLicenses: {
          ...state.companyLicenses,
          [`${country}/${identifier}`]: {
            ...currentState,
            licenseIds: [ ...currentState.licenseIds, license.id ],
            state: 'idle',
          },
        },
      };
    }
    case types.LICENSE.DEACTIVATE: {
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.DEACTIVATE}-${Date.now()}`,
        level: 'info',
        read: true,
        messageId: 'license.alert.deactivation.default',
        defaultMessage: 'Deactivating license.',
      };
      return {
        ...state,
        actionInProgress: true,
        actionLog: [log, ...state.actionLog]
      };
    }
    case types.LICENSE.DEACTIVATE_FAIL: {
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.DEACTIVATE_FAIL}-${Date.now()}`,
        level: 'error',
        read: false,
        messageId: 'license.alert.deactivation.fail',
        defaultMessage: 'Oops! License deactivation failed.',
      };
      return {
        ...state,
        actionInProgress: false,
        actionLog: [log, ...state.actionLog]
      };
    }
    case types.LICENSE.DEACTIVATE_SUCCESS: {
      const { licenseId, license } = action.payload;
      const items = state.licenses.items.reduce((acc, l) => {
        if (licenseId === l.id) {
          if (license) {
            acc.push(new License(license));
          }
        } else {
          acc.push(l);
        }
        return acc;
      }, []);
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.DEACTIVATE_SUCCESS}-${Date.now()}`,
        level: 'success',
        read: false,
        messageId: 'license.alert.deactivation.success',
        defaultMessage: 'License deactivated.',
      };
      return {
        ...state,
        licenses: {
          ...state.licenses,
          fetched: Date.now(),
          items,
        },
        actionInProgress: false,
        actionLog: [log, ...state.actionLog]
      };
    }
    case types.LICENSE.UPGRADE: {
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.UPGRADE}-${Date.now()}`,
        level: 'info',
        read: true,
        messageId: 'license.alert.upgrade.default',
        defaultMessage: 'Replacing license.',
      };
      return {
        ...state,
        actionInProgress: true,
        actionLog: [log, ...state.actionLog]
      };
    }
    case types.LICENSE.UPGRADE_FAIL: {
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.UPGRADE_FAIL}-${Date.now()}`,
        level: 'error',
        read: false,
        messageId: 'license.alert.upgrade.fail',
        defaultMessage: 'Oops! License upgrade failed.',
      };
      return {
        ...state,
        actionInProgress: false,
        actionLog: [log, ...state.actionLog]
      };
    }
    case types.LICENSE.UPGRADE_SUCCESS: {
      const { payload: { country, identifier, license, newLicense } } = action;
      const {
        companyLicenses: {
          [`${country}/${identifier}`]: currentState,
        }
      } = state;
      const items = state.licenses.items.reduce((acc, l) => {
        if (license.id === l.id) {
          acc.push(new License(license));
        } else {
          acc.push(l);
        }
        return acc;
      }, []);
      items.push(new License(newLicense));
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.UPGRADE_SUCCESS}-${Date.now()}`,
        level: 'success',
        read: false,
        messageId: 'license.alert.upgrade.success',
        defaultMessage: 'License upgraded.',
      };
      return {
        ...state,
        licenses: {
          ...state.licenses,
          fetched: Date.now(),
          items,
        },
        companyLicenses: {
          ...state.companyLicenses,
          [`${country}/${identifier}`]: {
            ...currentState,
            licenseIds: [ ...currentState.licenseIds, newLicense.id ],
            state: 'idle',
          },
        },
        actionInProgress: false,
        actionLog: [log, ...state.actionLog]
      };
    }
    case types.LICENSE.UPDATE: {
      const { payload: { updatedLicense } } = action;
      const items = state.licenses.items.reduce((acc, l) => {
        if (updatedLicense.id === l.id) {
          acc.push(new License(updatedLicense));
        } else {
          acc.push(l);
        }
        return acc;
      }, []);
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.UPDATE}-${Date.now()}`,
        level: 'info',
        read: true,
        messageId: 'license.alert.update.default',
        defaultMessage: 'Updating license.',
      };
      return {
        ...state,
        licenses: {
          ...state.licenses,
          items,
        },
        actionInProgress: true,
        actionLog: [log, ...state.actionLog]
      };
    }
    case types.LICENSE.UPDATE_FAIL: {
      const { payload: { license } } = action;
      const items = state.licenses.items.reduce((acc, l) => {
        if (license.id === l.id) {
          acc.push(new License(license));
        }
        acc.push(l);
        return acc;
      }, []);
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.UPDATE_FAIL}-${Date.now()}`,
        level: 'error',
        read: false,
        messageId: 'license.alert.update.fail',
        defaultMessage: 'Oops! License update failed.',
      };
      return {
        ...state,
        licenses: {
          ...state.licenses,
          items,
        },
        actionInProgress: false,
        actionLog: [log, ...state.actionLog]
      };
    }
    case types.LICENSE.UPDATE_SUCCESS: {
      const { payload: { updatedLicense } } = action;
      const items = state.licenses.items.reduce((acc, l) => {
        if (updatedLicense.id === l.id) {
          acc.push(new License(updatedLicense));
        } else {
          acc.push(l);
        }
        return acc;
      }, []);
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.LICENSE.UPDATE_SUCCESS}-${Date.now()}`,
        level: 'success',
        read: false,
        messageId: 'license.alert.update.success',
        defaultMessage: 'License updated.',
      };
      return {
        ...state,
        licenses: {
          ...state.licenses,
          fetched: Date.now(),
          items,
        },
        actionInProgress: false,
        actionLog: [log, ...state.actionLog]
      };
    }
    case types.SELLERS.FETCH: {
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.SELLERS.FETCH}-${Date.now()}`,
        level: 'info',
        read: true,
        messageId: 'sellers.alert.fetch.default',
        defaultMessage: 'Fetching sellers.',
      };
      return {
        ...state,
        actionInProgress: true,
        actionLog: [log, ...state.actionLog]
      }
    }
    case types.SELLERS.RECEIVE: {
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.SELLERS.RECEIVE}-${Date.now()}`,
        level: 'success',
        read: true,
        messageId: 'sellers.alert.fetch.success',
        defaultMessage: 'Sellers fetched.',
      };
      return {
        ...state,
        sellers: {
          ...state.sellers,
          fetched: true,
          items: action.payload.sellers,
        },
        actionInProgress: false,
        actionLog: [log, ...state.actionLog]
      }
    }
    case types.SELLERS.FAIL: {
      const log = {
        action: { ...action },
        created: Date.now(),
        id: `${types.SELLERS.FAIL}-${Date.now()}`,
        level: 'error',
        read: false,
        messageId: 'sellers.alert.fetch.fail',
        defaultMessage: 'Error fetching sellers.',
      };
      return {
        ...state,
        actionInProgress: false,
        actionLog: [log, ...state.actionLog]
      }
    }
    case types.LICENSE_PAYMENTS.SAVE: {
      return {
        ...state,
        payment: {
          ...state.payment,
          inProgress: true,
        }
      }
    }
    case types.LICENSE_PAYMENTS.FAIL: {
      return {
        ...state,
        payment: {
          ...state.payment,
          inProgress: false,
        }
      }
    }
    case types.LICENSE_PAYMENTS.RESULT: {
      const { billedLogs, paidLogs } = action.payload;
      const items = state.licenses.items.reduce((acc, l) => {
        const updated = [...billedLogs, ...paidLogs].find(u => u.id === l.id);
        if (updated) {
          acc.push(new License(updated));
        } else {
          acc.push(l);
        }
        return acc;
      }, []);
      const billed = billedLogs.map(l => l.id);
      const paid = paidLogs.map(l => l.id);
      const timestamp = Date.now();
      return {
        ...state,
        licenses: {
          ...state.licenses,
          fetched: timestamp,
          items,
        },
        payment: {
          ...state.payment,
          inProgress: false,
          updated: timestamp,
          paid,
          billed,
        }
      };
    }
    case types.LICENSE_PAYMENTS.FETCH: {
      return {
        ...state,
        payments: {
          ...state.payments,
          state: 'busy',
        }
      }
    }
    case types.LICENSE_PAYMENTS.RECEIVE: {
      return {
        ...state,
        payments: {
          ...state.payments,
          byLicenseId: {
            ...state.payments.byLicenseId,
            [action.payload.licenseId]: action.payload.payments,
          },
          state: 'idle',
        }
      }
    }
    case types.LICENSE_PAYMENTS.FETCH_FAIL: {
      const { error } = action.payload;
      const log = {
        created: Date.now(),
        id: `${types.LICENSE_PAYMENTS.FETCH_FAIL}-${Date.now()}`,
        level: 'error',
        read: false,
        messageId: 'licensePayments.alert.fetch.fail',
        defaultMessage: 'Fetching license payments failed: {error}',
        messageValues: { error },
      };
      return {
        ...state,
        actionLog: [log, ...state.actionLog],
        payments: { ...state.payments, state: 'idle' },
      }
    }
    case types.LICENSES.EXPORT: {
      return {
        ...state,
        actionInProgress: true,
      };
    }
    case types.LICENSES.EXPORT_FAIL: {
      return {
        ...state,
        actionInProgress: false,
      };
    }
    case types.LICENSES.EXPORT_SUCCESS: {
      return {
        ...state,
        actionInProgress: false,
      };
    }
    case types.LICENSES.IMPORT: {
      return {
        ...state,
        migration: {
          ...state.migration,
          inProgress: true,
        },
      };
    }
    case types.LICENSES.IMPORT_FAIL: {
      return {
        ...state,
        migration: {
          ...state.migration,
          error: {
            status: true,
          },
          inProgress: false,
        },
      };
    }
    case types.LICENSES.IMPORT_SUCCESS: {
      const { created, invalidRows, overwrite } = action.payload;
      const items = created.map(l => new License(l));
      const timestamp = Date.now();
      return {
        ...state,
        licenses: {
          ...state.licenses,
          fetched: timestamp,
          items: [ ...state.licenses.items, ...items ],
        },
        migration: {
          ...state.migration,
          created: created.map(l => l.id),
          error: {
            status: false,
          },
          inProgress: false,
          invalidRows,
          overwrite,
          updated: timestamp,
        },
      };
    }
    case types.ACTION_LOG.CLEAR: {
      const { al } = action.payload;
      const actionLog = state.actionLog.reduce((acc, i) => {
        if (i.id === al.id) {
          i.read = true;
        }
        acc.push(i);
        return acc;
      }, []);
      return {
        ...state,
        actionLog
      };
    }
    case types.REPORT_EDITABLE.SET: {
      // Set all license action logs as read when ever active
      // company changes.
      return {
        ...state,
        actionLog: state.actionLog.map(al => { al.read = true; return al; }),
      };
    }
    case types.CONTACT_PERSON.UPDATE: {
      const {
        payload: {
          contactPerson,
          country,
          identifier,
        },
      } = action;
      const {
        companyLicenses: {
          [`${country}/${identifier}`]: {
            licenseIds: companyLicenses,
          },
        },
        licenses: { items: oldLicenses },
      } = state;
      const updatedLicenses = oldLicenses.map(l => {
        if (companyLicenses.indexOf(l.data.id) !== -1) {
          if (l.contactPerson.id === contactPerson.id) {
            return new License({ ...l.data, contactPerson });
          }
        }
        return l;
      })
      return {
        ...state,
        licenses: { items: updatedLicenses },
      };
    }
    case types.CONTACT_PERSON.UPDATE_FAIL: {
      const {
        payload: {
          oldContactPerson: contactPerson,
          country,
          identifier,
        },
      } = action;
      const {
        companyLicenses: {
          [`${country}/${identifier}`]: {
            licenseIds: companyLicenses,
          },
        },
        licenses: { items: oldLicenses },
      } = state;
      const updatedLicenses = oldLicenses.map(l => {
        if (companyLicenses.indexOf(l.data.id) !== -1) {
          if (l.contactPerson.id === contactPerson.id) {
            return new License({ ...l.data, contactPerson });
          }
        }
        return l;
      })
      return {
        ...state,
        licenses: { items: updatedLicenses },
      };
    }
    default:
      return state;
  }
}

const loadingInitialState = {
  report: false,
};

// Loading indicators
function loading(state = loadingInitialState, action) {
  switch (action.type) {
    case types.REPORT.RECEIVE:
      return { ...state, report: false };
    case types.REPORT.FETCH:
      return { ...state, report: true };
    case types.REPORT.FAIL:
      return { ...state, report: false };
    default:
      return state;
  }
}

const errorsInitialState = {
  report: {
    error: false,
    reason: null,
  },
};

// Loading indicators
function errors(state = errorsInitialState, action) {
  switch (action.type) {
    case types.REPORT.RECEIVE:
    case types.REPORT.FETCH:
      return { ...state, report: { error: false, reason: null } };
    case types.REPORT.FAIL:
      return { ...state, report: { error: true, reason: action.payload.err} };
    default:
      return state;
  }
}

const language = navigator.language || navigator.userLanguage;
const lang =
  queryString.parse(location.search).lang ||
  localStorage.getItem('locale') ||
  language.substr(0, 2) ||
  'fi';
const localeInitialState = {
  lang,
};

function locale(state = localeInitialState, action) {
  switch (action.type) {
    case types.LOCALE.SET: {
      const parameters = queryString.parse(location.search);
      const query = queryString.stringify(
        Object.assign(parameters, { lang: action.payload }),
      );

      history.replaceState(null, '', `${window.location.pathname}?${query}`);
      localStorage.setItem('locale', action.payload);
      return { ...state, lang: action.payload };
    }
    case types.LOCALE.CLEAR:
      return { ...localeInitialState };
    default:
      return state;
  }
}

const reportInitialState = {};

function report(state = reportInitialState, action) {
  switch (action.type) {
    case types.REPORT.SET:
      return { ...action.payload };
    case types.REPORT.RECEIVE:
      return { ...state, ...action.payload, showSpinner: false };
    case types.REPORT.CLEAR:
      return { ...reportInitialState };
    case types.PORTFOLIO.RECEIVE_ENABLE:
      return { ...state, portfolio_enabled: true };
    case types.PORTFOLIO.DISABLE:
      return { ...state, portfolio_enabled: false };
    default:
      return state;
  }
}

// NOTE: Report edited is basically a copy of the fetched company report
// that holds the changes user makes to the report details

const reportEditableInitialState = null;

function reportEditable(state = reportEditableInitialState, action) {
  switch (action.type) {
    case types.REPORT_EDITABLE.SET:
      return { ...state, ...action.payload };
    case types.REPORT_EDITABLE.CLEAR:
      return { ...reportEditableInitialState };
    case types.EDITING.CLEAR:
      return { ...reportEditableInitialState };
    default:
      return state;
  }
}

const localitiesInitialState = {
  data: {},
};

function localities(state = localitiesInitialState, action) {
  switch (action.type) {
    case types.LOCALITIES.RECEIVE:
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

const regionsInitialState = {
  data: {},
};

function regions(state = regionsInitialState, action) {
  switch (action.type) {
    case types.REGIONS.RECEIVE:
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

const dashboardInitialState = {
  authenticationForms: [],
  editMode: false,
  chats: [],
};

function dashboard(state = dashboardInitialState, action) {
  switch (action.type) {
    case types.AUTH.RECEIVE:
      return { ...state, authenticationForms: action.payload.forms };
    // TODO what other actions clear editing mode?
    case types.EDITING.SET:
      return { ...state, editMode: true };
    case types.EDITING.CLEAR:
      return { ...state, editMode: false };
    case types.CHAT.RECEIVELIST:
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

const reviewInitialState = {
  user_id: null,
  source: null,
  partner: null,
  name: null,
  picture: null,
  overall: 0,
  thumbs: 0,
  quality: 0,
  price: 0,
};

function review(state = reviewInitialState, action) {
  switch (action.type) {
    case types.REVIEW.SET: {
      const { ratingType, ratingVal } = action.payload;
      const newState = { ...state };
      newState[ratingType] = ratingVal;
      return newState;
    }
    case types.REVIEW.CLEAR:
      return { ...reviewInitialState };
    case types.REVIEW.RECEIVE:
      return { ...reviewInitialState, ...action.payload };
    default:
      return state;
  }
}

const companyReviewsInitialState = {};

function companyReviews(state = companyReviewsInitialState, action) {
  switch (action.type) {
    case types.COMPANY_REVIEWS.SET:
      return { ...action.payload };
    case types.COMPANY_REVIEWS.RECEIVE:
      return { ...action.payload };
    case types.COMPANY_REVIEWS.CLEAR:
      return { ...companyReviewsInitialState };
    default:
      return state;
  }
}

const userReviewsInitialState = {};

function userReviews(state = userReviewsInitialState, action) {
  switch (action.type) {
    case types.USER_REVIEWS.SET:
      return { ...action.payload };
    case types.USER_REVIEWS.RECEIVE:
      return { ...action.payload };
    case types.USER_REVIEWS.CLEAR:
      return { ...userReviewsInitialState };
    default:
      return state;
  }
}

const chatInitialState = {
  id: null,
  company: null,
  review: null,
  messages: [],
  actionLog: [],
};

function chat(state = chatInitialState, action) {
  switch (action.type) {
    case types.CHAT.RECEIVE:
      return { ...state, ...action.payload };
    case types.CHAT.SAVE:
      return { ...state, messages: [...state.messages, action.payload] };
    case types.CHAT.FETCH_FAIL: {
      const timestamp = Date.now();
      const log = {
        id: `${action.type}-${timestamp}`,
        level: 'error',
        read: false,
        messageId: 'chat.alert.fetch.fail',
        defaultMessage: 'Failed to fetch chat.',
      };
      return { ...state, actionLog: [...state.actionLog, log] };
    }
    case types.ACTION_LOG.CLEAR: {
      const { al } = action.payload;
      const actionLog = state.actionLog.reduce((acc, i) => {
        if (i.id === al.id) {
          i.read = true;
        }
        acc.push(i);
        return acc;
      }, []);
      return {
        ...state,
        actionLog
      };
    }
    default:
      return state;
  }
}

const topReviewersInitialState = {};

function topReviewers(state = topReviewersInitialState, action) {
  switch (action.type) {
    case types.TOP_REVIEWERS.RECEIVE:
      return { ...action.payload };
    default:
      return state;
  }
}

const topCompaniesInitialState = {};

function topCompanies(state = topCompaniesInitialState, action) {
  switch (action.type) {
    case types.TOP_COMPANIES.RECEIVE:
      return { ...action.payload };
    default:
      return state;
  }
}

const portfolioInitialState = {
  portfolio: {},
  slidesCopy: [],
  loading: false,
  error: false,
};

const newSlide = { title: '', description: '', image_id: null , order_no: 0 };

function portfolio(state = portfolioInitialState, action) {
  switch (action.type) {
    // Remember old slides so that it can be recovered if user cancels
    case types.EDITING.SET:
      if (!state.portfolio.slides) return { ...state };
      return { ...state, slidesCopy: [...state.portfolio.slides] };
    case types.PORTFOLIO.FETCH:
      return { ...state, loading: true };
    case types.PORTFOLIO.FAIL:
      return { ...state, loading: false, error: true };
    case types.PORTFOLIO.RECEIVE:
      return { ...state, loading: false, portfolio: action.payload };
    case types.PORTFOLIO.RESULT:
      return {
        ...state,
        loading: false,
        portfolio: action.payload.portfolio,
      };
    case types.PORTFOLIO.REMOVE_SLIDE: {
      const { payload: { slideId } } = action;
      const slide = state.portfolio.slides.find(s => s.id === slideId);
      const deletedOrderNo = slide.order_no;
      const slides = state.portfolio.slides
        .filter(s => s.id !== slideId)
        .map(s => {
          // Adjust the order number for slides after the deleted slide.
          let orderNo = s.order_no;
          if (s.order_no > deletedOrderNo) {
            orderNo -= 1;
          }
          return { ...s, order_no: orderNo };
        });
      return { ...state, portfolio: { ...state.portfolio, slides } };
    }
    case types.PORTFOLIO.RECOVER_SLIDES:
      return {
        ...state,
        slidesCopy: [],
        portfolio: { ...state.portfolio, slides: [...state.slidesCopy] },
      };
    case types.PORTFOLIO.UPDATE_FIELD: {
      const { payload: { field, slideId, value } } = action;
      const updatedSlide = state.portfolio.slides.find(s => s.id === slideId);
      // Construct new slides representation.
      const slides = state.portfolio.slides
        .map(slide => {
          // Update the slide in question.
          if (slide.id !== slideId) return slide;
          let sanitizedValue = value;
          if (field === 'description') {
            sanitizedValue = value.slice(0, 550);
          }
          return { ...slide, [field]: sanitizedValue };
        })
        .map(slide => {
          // If the ordering has changed, we must adjust the order numbers for
          // the slides between the from and to spots of the updated slide.
          // Also skip the updated slide here, because its order number was
          // updated already above.
          if (field !== 'order_no' || slide.id === slideId) return slide;
          const upperLimit = Math.max(updatedSlide.order_no, value);
          const lowerLimit = Math.min(updatedSlide.order_no, value);
          if (slide.order_no <= upperLimit && slide.order_no >= lowerLimit) {
            const orderNo = (
              updatedSlide.order_no < value
                ? slide.order_no - 1
                : slide.order_no + 1
            );
            return { ...slide, order_no: orderNo};
          }
          return slide;
        });
      return { ...state, portfolio: { ...state.portfolio, slides } };
    }
    case types.PORTFOLIO.UPDATE_COMPANY_FIELD:
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          company: {
            ...state.portfolio.company,
            [action.payload.field]: action.payload.value,
          },
        },
      };
    case types.PORTFOLIO.RECEIVE_LOGO:
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          company: {
            ...state.portfolio.company,
            logo_id: action.payload,
          },
        },
      };
    case types.PORTFOLIO.ADD_SLIDE: {
      const order = state.portfolio.slides.length;
      // Temporary id needed for updating before the slide is saved to DB.
      let slideId = 1;
      if (state.portfolio.slides.length) {
        slideId = Math.max(...state.portfolio.slides.map(s => s.id)) + 1;
      }
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          slides: [
            ...state.portfolio.slides,
            { ...newSlide, order_no: order, id: slideId },
          ],
        },
      };
    }
    case types.PORTFOLIO.RECEIVE_IMAGE: {
      const { slideId, imgId } = action.payload;
      const slides = state.portfolio.slides.map(slide => {
        if (slide.id !== slideId) return slide;
        return { ...slide, image_id: imgId };
      });

      return { ...state, portfolio: { ...state.portfolio, slides } };
    }
    default:
      return state;
  }
}

const paidInitialState = {
  keywords: [],
  keywordsEditable: [],
  // localities is an array of printable operational areas
  // [{ id, name_fi, name_sv, type[MUNICIPALITY/REGION], is_hometown}]
  localities: [],
  // regions is an id based mapping of regions where the company operates.
  // each region has whole_region -flag indicating whether the company
  // operates on the whole region, and an id based mapping of the municipalities
  // within the region that the company operates in.
  regions: {},
  // union of above mentioned municipalities mappings within each region
  // company hometown is marked with is_hometown flag
  municipalities: {},
};

// Unpack company locality data.
// Separate municipalites into their own and generate a locality-list
// containing whole-regions and specific municipalities
// ***
// Accepts id based object mapping of regions, or an array of regions,
// with municipalities contained with the same logic.
const unpackLocalities = regionsCollection => {
  const unpacked = {
    localities: [],
    regions: {},
    municipalities: {},
  };

  // Check if already an id based object mapping or an array
  // If an array, convert.
  let regionsMap = {};
  if (Array.isArray(regionsCollection)) {
    regionsCollection.forEach(r => {
      regionsMap[r.id] = r;
    });
  } else {
    regionsMap = regionsCollection;
  }

  Object.values(regionsMap).forEach(r => {
    // Helper function to try'n'DRY later yo
    const handleMunicipality = m => {
      if (m.is_hometown) {
        r.is_homeregion = true;
      }
      if (!r.whole_region || m.is_hometown) {
        unpacked.localities.push({ ...m, type: 'MUNICIPALITY' });
      }
    };

    // Check if already an id based object mapping or an array
    // If an array, convert.
    if (Array.isArray(r.municipalities)) {
      const municipalitiesMap = {};
      r.municipalities.forEach(m => {
        municipalitiesMap[m.id] = m;
        handleMunicipality(m); // DRY
      });
      r.municipalities = municipalitiesMap;
    } else {
      Object.values(r.municipalities).forEach(handleMunicipality); // DRY
    }

    unpacked.regions[r.id] = r;
    if (r.whole_region) {
      unpacked.localities.push({
        id: r.id,
        name_fi: r.name_fi,
        name_sv: r.name_sv,
        type: 'REGION',
      });
    }

    Object.assign(unpacked.municipalities, r.municipalities);
  });

  return unpacked;
}

function paidFeatures(state = paidInitialState, action) {
  switch (action.type) {
    case types.EDITING.SET: {
      const keywordsEditable = [];
      state.keywords.forEach((value, id) => {
        keywordsEditable.push({ value, id });
      });
      if (keywordsEditable.length < 30) {
        keywordsEditable.push({ value: '', id: null });
      }
      return { ...state, keywordsEditable };
    }
    case types.PAID.RECEIVE_KEYWORDS: {
      const keywordsEditable = [];
      action.payload.forEach((value, id) => {
        keywordsEditable.push({ value, id });
      });
      if (keywordsEditable.length < 30) {
        keywordsEditable.push({ value: '', id: null });
      }
      return { ...state, keywords: action.payload, keywordsEditable };
    }
    case types.PAID.UPDATE_KEYWORDS: {
      const { id: replaceThis, value } = action.payload;
      const keywordsEditable = [];
      let counter = 0;
      state.keywordsEditable.forEach((kw) => {
        if (kw.id === replaceThis) {
          if (value !== null) {
            if (replaceThis === null) {
              keywordsEditable.push({
                value,
                id: state.keywordsEditable.length - 1
              });
            } else {
              keywordsEditable.push({
                value,
                id: kw.id === null ? null : counter,
              });
              counter++;
            }
          }
        } else {
          keywordsEditable.push({
            value: kw.value,
            id: kw.id === null ? null : counter,
          });
          counter++;
        }
      })
      if (keywordsEditable[keywordsEditable.length - 1].id !== null
        && keywordsEditable.length < 30) {
        keywordsEditable.push({ value: '', id: null });
      }
      return { ...state, keywordsEditable };
    }
    case types.PAID.SAVE_KEYWORDS:
      const keywords = state.keywordsEditable.reduce((acc, keyword) => {
        const trimmed = keyword.value.trim();
        if (trimmed) acc.push(trimmed);
        return acc;
      }, []);
      return { ...state, keywords };
    case types.PAID.RECEIVE_COMPANY_REGIONS:
    case types.PAID.SAVE_COMPANY_REGIONS:
    case types.PAID.FAIL_SAVE_COMPANY_REGIONS: {
      // localities come in the following formation:
      // [regions(id, name_fi, name_sv, whole_region,
      //   [municipalities(id, name_fi, name_sv, is_hometown)])]
      const unpacked = unpackLocalities(action.payload.regions);
      return { ...state, ...unpacked };
    }
    default:
      return state;
  }
}

const contactPersonsInitialState = {
  added: null,
  addError: false,
  addInProgress: false,
  fetched: null,
  fetchError: false,
  fetchInProgress: false,
  updated: null,
  updateError: false,
  updateInProgress: false,
  actionLog: [],
  byCompanyId: {},
  latestAction: null,
}

function contactPersons(state = contactPersonsInitialState, action) {
  switch (action.type) {
    case types.CONTACT_PERSONS.FETCH: {
      return {
        ...state,
        fetchInProgress: true,
      };
    }
    case types.CONTACT_PERSONS.FAIL: {
      const timestamp = Date.now();
      const log = {
        id: `${action.type}-${timestamp}`,
        level: 'error',
        read: false,
        messageId: 'contactPerson.alert.fetch.fail',
        defaultMessage: 'Fetching contact persons failed.',
      };
      return {
        ...state,
        fetched: timestamp,
        fetchError: true,
        fetchInProgress: false,
        latestAction: timestamp,
        actionLog: [ ...state.actionLog, log ],
      };
    }
    case types.CONTACT_PERSONS.RECEIVE: {
      const timestamp = Date.now();
      const { payload: { contactPersons: items, companyId }} = action;
      return {
        ...state,
        fetched: timestamp,
        fetchError: false,
        fetchInProgress: false,
        byCompanyId: { ...state.byCompanyId, [companyId]: items },
        latestAction: timestamp,
      };
    }
    case types.CONTACT_PERSON.ADD: {
      return {
        ...state,
        addInProgress: true,
      };
    }
    case types.CONTACT_PERSON.ADD_FAIL: {
      const { payload: { error }, type } = action;
      const timestamp = Date.now();
      const log = {
        id: `${type}-${timestamp}`,
        level: 'error',
        read: false,
        messageId: 'contactPerson.alert.add.fail',
        defaultMessage: 'Saving new contact person failed: {error}',
        messageValues: { error },
      };
      return {
        ...state,
        added: timestamp,
        addError: true,
        addInProgress: false,
        latestAction: timestamp,
        actionLog: [ ...state.actionLog, log ],
      };
    }
    case types.CONTACT_PERSON.ADD_SUCCESS: {
      const timestamp = Date.now();
      const log = {
        id: `${action.type}-${timestamp}`,
        level: 'success',
        read: false,
        messageId: 'contactPerson.alert.add.success',
        defaultMessage: 'Saving new contact person success.',
      };
      const { payload: { contactPerson, companyId }} = action;
      const items = state.byCompanyId[companyId] || [];
      contactPerson.timestamp = timestamp;
      items.push(contactPerson);
      return {
        ...state,
        added: timestamp,
        addError: false,
        addInProgress: false,
        byCompanyId: {
          ...state.byCompanyId,
          [companyId]: items,
        },
        latestAction: timestamp,
        actionLog: [ ...state.actionLog, log ],
      };
    }
    case types.CONTACT_PERSON.UPDATE: {
      return {
        ...state,
        updateInProgress: true,
      };
    }
    case types.CONTACT_PERSON.UPDATE_FAIL: {
      const timestamp = Date.now();
      const log = {
        id: `${action.type}-${timestamp}`,
        level: 'error',
        read: false,
        messageId: 'contactPerson.alert.update.fail',
        defaultMessage: 'Updating contact person failed.',
      };
      return {
        ...state,
        updated: timestamp,
        updateError: true,
        updateInProgress: false,
        latestAction: timestamp,
        actionLog: [ ...state.actionLog, log ],
      };
    }
    case types.CONTACT_PERSON.UPDATE_SUCCESS: {
      const timestamp = Date.now();
      const log = {
        id: `${action.type}-${timestamp}`,
        level: 'success',
        read: false,
        messageId: 'contactPerson.alert.update.success',
        defaultMessage: 'Updating contact person success.',
      };
      const { payload: { contactPerson, companyId }} = action;
      const items = state.byCompanyId[companyId]
        .map(cp => {
          if (cp.id === contactPerson.id) {
            return { ...contactPerson, timestamp };
          }
          return { ...cp, timestamp: null };
        });
      return {
        ...state,
        updated: timestamp,
        updateError: false,
        updateInProgress: false,
        byCompanyId: {
          ...state.byCompanyId,
          [companyId]: items,
        },
        latestAction: timestamp,
        actionLog: [ ...state.actionLog, log ],
      };
    }
    case types.ACTION_LOG.CLEAR: {
      const { al } = action.payload;
      const actionLog = state.actionLog.reduce((acc, i) => {
        if (i.id === al.id) {
          i.read = true;
        }
        acc.push(i);
        return acc;
      }, []);
      return {
        ...state,
        actionLog
      };
    }
    default:
      return state;
  }
}


const quickChecksInitialState = {
  byCompanyId: {},
};

function quickChecks(state = quickChecksInitialState, action) {
  switch (action.type) {
    case types.RP_QUICK_CHECK.RECEIVE: {
      return {
        ...state,
        byCompanyId: {
          ...state.byCompanyId,
          [action.payload.company.id]: action.payload.quickCheck,
        },
      };
    }
    case types.RP_QUICK_CHECK.FAIL: {
      return {
        ...state,
        byCompanyId: {
          ...state.byCompanyId,
          [action.payload.company.id]: null,
        },
      };
    }
    default:
      return state;
  }
}

const rootReducer = combineReducers({
  session,
  loading,
  locale,
  localities,
  regions,
  report,
  reportEditable,
  review,
  companyReviews,
  userReviews,
  chat,
  dashboard,
  topReviewers,
  topCompanies,
  portfolio,
  paidFeatures,
  errors,
  licenses,
  contactPersons,
  quickChecks,
});

export default rootReducer;
